import React, { useState, useEffect } from 'react';
import {
  Heart,
  Search,
  Plus,
  Eye,
  X,
  Edit2,
  Trash2
} from 'lucide-react';
import api from '../../utils/api';
import './HealthPage.css';

const HealthPage = () => {
  // State for stats
  const [stats, setStats] = useState({
    studentsWithAllergies: 0,
    bloodGroupDistribution: {}
  });

  // State for health records
  const [healthRecords, setHealthRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    searchStudent: '',
    class: ''
  });

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    studentId: '',
    bloodGroup: '',
    height: '',
    weight: '',
    allergies: '',
    medications: '',
    conditions: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    notes: ''
  });

  // Constants
  const CLASSES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
  const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const [students, setStudents] = useState([]);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/health/stats');
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch health records
  const fetchHealthRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.searchStudent) params.append('student', filters.searchStudent);
      if (filters.class) params.append('class', filters.class);

      const response = await api.get(`/health?${params.toString()}`);
      const data = response.data.items || response.data;
      setHealthRecords(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching health records:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch students for autocomplete
  const fetchStudents = async (search) => {
    try {
      const response = await api.get(`/students/search?q=${search}`);
      const data = response.data;
      setStudents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchHealthRecords();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchStudent = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      searchStudent: value
    }));
    if (value.length > 2) {
      fetchStudents(value);
      setShowStudentDropdown(true);
    } else {
      setShowStudentDropdown(false);
    }
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddStudentSelect = (student) => {
    setFormData(prev => ({
      ...prev,
      studentId: student.id
    }));
    setShowStudentDropdown(false);
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      bloodGroup: '',
      height: '',
      weight: '',
      allergies: '',
      medications: '',
      conditions: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelation: '',
      notes: ''
    });
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    try {
      if (selectedRecord) {
        // Edit mode
        await api.put(`/health/${selectedRecord.id}`, formData);
        setMessage('Health record updated successfully');
      } else {
        // Add mode
        await api.post('/health', formData);
        setMessage('Health record added successfully');
      }
      setShowAddModal(false);
      resetForm();
      setSelectedRecord(null);
      fetchHealthRecords();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error saving health record:', err);
      setError('Failed to save health record');
    }
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setFormData({
      studentId: record.studentId,
      bloodGroup: record.bloodGroup,
      height: record.height,
      weight: record.weight,
      allergies: record.allergies || '',
      medications: record.medications || '',
      conditions: record.conditions || '',
      emergencyContactName: record.emergencyContactName || '',
      emergencyContactPhone: record.emergencyContactPhone || '',
      emergencyContactRelation: record.emergencyContactRelation || '',
      notes: record.notes || ''
    });
    setShowAddModal(true);
  };

  const handleDeleteRecord = async () => {
    try {
      await api.delete(`/health/${selectedRecord.id}`);
      setMessage('Health record deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedRecord(null);
      fetchHealthRecords();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting health record:', err);
      setError('Failed to delete health record');
    }
  };

  const getAllergiesBadges = (allergiesStr) => {
    if (!allergiesStr) return [];
    return allergiesStr.split(',').map(a => a.trim()).filter(a => a);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowDetailModal(false);
    setShowDeleteConfirm(false);
    if (!showAddModal) {
      setSelectedRecord(null);
      resetForm();
    }
  };

  return (
    <div className="health-page">
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card stat-allergies">
          <div className="stat-icon">
            <Heart size={24} color="#ef4444" />
          </div>
          <div className="stat-content">
            <h3>Students with Allergies</h3>
            <p className="stat-amount">{stats.studentsWithAllergies || 0}</p>
          </div>
        </div>

        <div className="stat-card stat-blood">
          <div className="stat-icon">
            <Heart size={24} color="#b8860b" />
          </div>
          <div className="stat-content">
            <h3>Blood Group Distribution</h3>
            <div className="stat-detail">
              {Object.entries(stats.bloodGroupDistribution || {}).slice(0, 3).map(([group, count]) => (
                <span key={group}>{group}: {count}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div className="filter-bar">
            <div className="filter-group">
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search student name..."
                  name="searchStudent"
                  value={filters.searchStudent}
                  onChange={handleSearchStudent}
                />
                {showStudentDropdown && students.length > 0 && (
                  <div className="student-dropdown">
                    {students.map(student => (
                      <div
                        key={student.id}
                        className="dropdown-item"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            searchStudent: student.name
                          }));
                          setShowStudentDropdown(false);
                        }}
                      >
                        {student.name} - {student.class}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <select
                className="form-control"
                name="class"
                value={filters.class}
                onChange={handleFilterChange}
              >
                <option value="">All Classes</option>
                {CLASSES.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setSelectedRecord(null);
                setShowAddModal(true);
              }}
            >
              <Plus size={18} /> Add Health Record
            </button>
          </div>
        </div>
      </div>

      {/* Health Records Table */}
      <div className="card">
        <div className="card-header">
          <h2>Health Records</h2>
          <span className="record-count">{healthRecords.length} records</span>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="empty-state">
              <p>Loading health records...</p>
            </div>
          ) : healthRecords.length === 0 ? (
            <div className="empty-state">
              <Heart size={48} />
              <h3>No health records found</h3>
              <p>No records match your current filters</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="health-table">
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Class</th>
                    <th>Blood Group</th>
                    <th>Height (cm)</th>
                    <th>Weight (kg)</th>
                    <th>Allergies</th>
                    <th>Emergency Contact</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {healthRecords.map(record => (
                    <tr key={record.id}>
                      <td className="student-name">{record.studentName}</td>
                      <td>{record.class}</td>
                      <td>
                        <span className="badge badge-primary">{record.bloodGroup}</span>
                      </td>
                      <td>{record.height || '-'}</td>
                      <td>{record.weight || '-'}</td>
                      <td>
                        <div className="allergies-badges">
                          {getAllergiesBadges(record.allergies).length > 0 ? (
                            getAllergiesBadges(record.allergies).map((allergy, idx) => (
                              <span key={idx} className="badge badge-warning">{allergy}</span>
                            ))
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="contact-info">
                          {record.emergencyContactName && (
                            <>
                              <div>{record.emergencyContactName}</div>
                              <div className="text-muted">{record.emergencyContactPhone}</div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="actions">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setSelectedRecord(record);
                            setShowDetailModal(true);
                          }}
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleEditRecord(record)}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            setSelectedRecord(record);
                            setShowDeleteConfirm(true);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedRecord ? 'Edit Health Record' : 'Add Health Record'}</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddRecord}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Student</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search and select student..."
                    value={
                      formData.studentId
                        ? students.find(s => s.id === formData.studentId)?.name || ''
                        : ''
                    }
                    onChange={(e) => {
                      if (e.target.value.length > 2) {
                        fetchStudents(e.target.value);
                        setShowStudentDropdown(true);
                      }
                    }}
                    disabled={!!selectedRecord}
                    required
                  />
                  {showStudentDropdown && students.length > 0 && (
                    <div className="student-dropdown">
                      {students.map(student => (
                        <div
                          key={student.id}
                          className="dropdown-item"
                          onClick={() => handleAddStudentSelect(student)}
                        >
                          {student.name} - {student.class}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Blood Group</label>
                    <select
                      className="form-control"
                      name="bloodGroup"
                      value={formData.bloodGroup}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Blood Group</option>
                      {BLOOD_GROUPS.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Height (cm)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="height"
                      value={formData.height}
                      onChange={handleFormChange}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Weight (kg)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="weight"
                      value={formData.weight}
                      onChange={handleFormChange}
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Allergies (comma-separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleFormChange}
                    placeholder="e.g., peanuts, dairy, shellfish"
                  />
                </div>

                <div className="form-group">
                  <label>Medications (comma-separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="medications"
                    value={formData.medications}
                    onChange={handleFormChange}
                    placeholder="e.g., Aspirin 100mg, Insulin"
                  />
                </div>

                <div className="form-group">
                  <label>Medical Conditions (comma-separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="conditions"
                    value={formData.conditions}
                    onChange={handleFormChange}
                    placeholder="e.g., Asthma, Diabetes"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Emergency Contact Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="emergencyContactName"
                      value={formData.emergencyContactName}
                      onChange={handleFormChange}
                      placeholder="Full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Emergency Contact Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      name="emergencyContactPhone"
                      value={formData.emergencyContactPhone}
                      onChange={handleFormChange}
                      placeholder="Phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Emergency Contact Relation</label>
                    <input
                      type="text"
                      className="form-control"
                      name="emergencyContactRelation"
                      value={formData.emergencyContactRelation}
                      onChange={handleFormChange}
                      placeholder="e.g., Mother, Father"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows="3"
                    placeholder="Additional health information..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModals}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {selectedRecord ? 'Update Record' : 'Add Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRecord && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Health Record Details</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Basic Information</h3>
                <div className="detail-row">
                  <span>Student:</span>
                  <strong>{selectedRecord.studentName}</strong>
                </div>
                <div className="detail-row">
                  <span>Class:</span>
                  <strong>{selectedRecord.class}</strong>
                </div>
                <div className="detail-row">
                  <span>Blood Group:</span>
                  <strong>{selectedRecord.bloodGroup}</strong>
                </div>
              </div>

              <div className="detail-section">
                <h3>Physical Measurements</h3>
                <div className="detail-row">
                  <span>Height:</span>
                  <strong>{selectedRecord.height ? selectedRecord.height + ' cm' : '-'}</strong>
                </div>
                <div className="detail-row">
                  <span>Weight:</span>
                  <strong>{selectedRecord.weight ? selectedRecord.weight + ' kg' : '-'}</strong>
                </div>
              </div>

              {selectedRecord.allergies && (
                <div className="detail-section">
                  <h3>Allergies</h3>
                  <div className="allergies-badges">
                    {getAllergiesBadges(selectedRecord.allergies).map((allergy, idx) => (
                      <span key={idx} className="badge badge-warning">{allergy}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedRecord.medications && (
                <div className="detail-section">
                  <h3>Current Medications</h3>
                  <div className="detail-content">
                    {selectedRecord.medications.split(',').map((med, idx) => (
                      <div key={idx}>{med.trim()}</div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRecord.conditions && (
                <div className="detail-section">
                  <h3>Medical Conditions</h3>
                  <div className="detail-content">
                    {selectedRecord.conditions.split(',').map((cond, idx) => (
                      <div key={idx}>{cond.trim()}</div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRecord.emergencyContactName && (
                <div className="detail-section">
                  <h3>Emergency Contact</h3>
                  <div className="detail-row">
                    <span>Name:</span>
                    <strong>{selectedRecord.emergencyContactName}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Phone:</span>
                    <strong>{selectedRecord.emergencyContactPhone}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Relation:</span>
                    <strong>{selectedRecord.emergencyContactRelation}</strong>
                  </div>
                </div>
              )}

              {selectedRecord.notes && (
                <div className="detail-section">
                  <h3>Notes</h3>
                  <p>{selectedRecord.notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModals}
              >
                Close
              </button>
              <button
                className="btn btn-info"
                onClick={() => {
                  setShowDetailModal(false);
                  handleEditRecord(selectedRecord);
                }}
              >
                <Edit2 size={16} /> Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedRecord && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the health record for <strong>{selectedRecord.studentName}</strong>?</p>
              <p className="text-muted">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteRecord}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthPage;
